import type { LucideIcon } from "lucide-react";
import { cx } from "../../lib/cx";

/**
 * The one way to draw a lucide icon (plan D05).
 *
 * ── STROKE IS NOT A PROP ────────────────────────────────────────────────────
 * A2 drew icons at `STROKE` (2), then 2.25 and 2.5 wherever a screen wanted
 * emphasis, and the weights drifted apart screen by screen. Here there are two
 * weights and no third: the default, and `bold` for a glyph that has to hold
 * its own inside a filled control (a check in a chip, −/+ on a stepper).
 *
 * ── COLOUR IS THE PARENT'S ──────────────────────────────────────────────────
 * The SVG strokes `currentColor`, and nothing here sets a colour. The colour
 * comes from a token text class, on the icon (`className="text-ink-3"`) or on
 * an ancestor. Rule 10 refuses `fill-current`/`stroke-current` and every
 * literal, so there is no other way to colour it anyway.
 *
 * ── LABEL OR HIDDEN ─────────────────────────────────────────────────────────
 * Without `label` the icon is decoration (`aria-hidden`): the control around
 * it carries the name. With `label` it is an image with that name — only for
 * an icon that stands alone and means something no text beside it says.
 */

export const ICON_STROKE = 2;
export const ICON_STROKE_BOLD = 2.5;

export type IconSize = 16 | 18 | 20 | 24;

export interface IconProps {
  bold?: boolean;
  className?: string;
  icon: LucideIcon;
  /** The accessible name. Omit it when a text or a control already names the icon. */
  label?: string;
  size?: IconSize;
}

export function Icon({ icon, size, label, bold, className }: IconProps) {
  // A capitalised binding, so JSX reads it as a component.
  const Glyph = icon;
  const stroke = bold ? ICON_STROKE_BOLD : ICON_STROKE;
  if (label) {
    return (
      <Glyph
        aria-label={label}
        className={cx("shrink-0", className)}
        role="img"
        size={size ?? 20}
        strokeWidth={stroke}
      />
    );
  }
  return (
    <Glyph
      aria-hidden="true"
      className={cx("shrink-0", className)}
      focusable="false"
      size={size ?? 20}
      strokeWidth={stroke}
    />
  );
}
